import React, { useState } from 'react';
import { Shield, FileText, Copyright, Award, ArrowRight } from 'lucide-react';
import RegistrationModal from './RegistrationModal';

const WhatIsTrademarkSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const types = [
    {
      icon: FileText,
      title: 'Word Marks',
      description: 'Business titles, slogans, and text-based identifiers that distinguish your products or services'
    },
    {
      icon: Copyright,
      title: 'Design Marks',
      description: 'Logos, symbols, color combinations, and visual branding elements that represent your identity'
    },
    {
      icon: Shield,
      title: 'Service Marks',
      description: 'Identifiers specifically associated with service-based businesses instead of physical products'
    },
    {
      icon: Award,
      title: 'Collective Marks',
      description: 'Marks used collectively by members of an organization or group'
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div className="flex flex-col">
            <div className="inline-block bg-[#168356]/10 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Understanding Trademarks
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              What is a Trademark?
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                A trademark refers to a unique word, phrase, symbol, design, logo, or sign that identifies and differentiates the origin of one company's goods or services from another. It serves as the signature identity of your brand in the business marketplace.
              </p>
              <p>
                Take famous examples such as Nike's swoosh, Apple's apple symbol, or McDonald's golden arches. These marks are instantly identifiable and are legally protected, ensuring no other business can use similar confusing branding.
              </p>
              <p>
                In the United States, trademark ownership is obtained either through actual commercial usage or by submitting an application to the United States Patent and Trademark Office (USPTO). Once officially registered, protection can continue indefinitely as long as the mark is actively used and properly maintained.
              </p>
              <p className="font-semibold text-black">
                A strong trademark not only secures your brand legally but also enhances customer trust, strengthens recognition, and adds long-term value to your business.
              </p>
            </div>
          </div>

          {/* Right Content - Types */}
          <div className="flex flex-col justify-between h-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-6">
              <h3 className="text-2xl font-bold text-black mb-6">Types of Trademarks</h3>
              <div className="space-y-6">
                {types.map((type, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#168356]/10 rounded-lg flex items-center justify-center">
                        <type.icon className="w-6 h-6 text-[#168356]" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">{type.title}</h4>
                      <p className="text-gray-600 text-sm">{type.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Small CTA - Aligned to bottom */}
            <div className="bg-gradient-to-r from-[#168356] to-[#0a5938] rounded-2xl shadow-xl p-6 text-white mt-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold text-lg mb-1">Ready to Protect Your Brand?</h4>
                  <p className="text-white/90 text-sm">Start your trademark registration process today</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white text-[#d29b00] px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default WhatIsTrademarkSection;